//
//  CsvSummarySelector.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { 
  Box,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FC } from "react";
import { FormErrorMessage } from "./FormErrorMessage";
import { csvSummaryItems, csvSummaryGroups } from "../constants/csv";

type CsvSummarySelectorProps = {
  selectedCsvSummaryItems: string[];
  setSelectedCsvSummaryItems: (types: string[]) => void;
  errorMsg?: string;
};

export const CsvSummarySelector: FC<CsvSummarySelectorProps> = ({
  selectedCsvSummaryItems,
  setSelectedCsvSummaryItems,
  errorMsg,
}) => {
  const handleToggleAll = () => {
    if (selectedCsvSummaryItems.length === csvSummaryItems.length) {
      // All selected, so deselect all
      setSelectedCsvSummaryItems([]);
    } else {
      // Some or none selected, so select all
      setSelectedCsvSummaryItems(csvSummaryItems.map(item => item.type));
    }
  };

  const handleItemToggle = (itemType: string) => {
    if (selectedCsvSummaryItems.includes(itemType)) {
      setSelectedCsvSummaryItems(selectedCsvSummaryItems.filter(type => type !== itemType));
    } else {
      setSelectedCsvSummaryItems([...selectedCsvSummaryItems, itemType]);
    }
  };

  const allSelected = selectedCsvSummaryItems.length === csvSummaryItems.length;
  const someSelected = selectedCsvSummaryItems.length > 0;

  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{ width: 600 }}>
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2,
          backgroundColor: '#f8f9fa'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            ダウンロードしたいデータ項目にチェックし、リクエストボタンを押してください。
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={handleToggleAll}
            />
          }
          label="日次データを全て選択/解除"
          sx={{ mb: 2, fontWeight: 'bold' }}
        />
        {csvSummaryGroups.map((group) => (
          <Accordion key={group.title} defaultExpanded sx={{ mb: 1, boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#e3f2fd',
                minHeight: 48,
                '&.Mui-expanded': {
                  minHeight: 48,
                },
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                  '&.Mui-expanded': {
                    margin: '8px 0',
                  },
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {group.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1, pb: 1 }}>
              <FormGroup>
                {group.items.map((item) => (
                  <FormControlLabel
                    key={item.type}
                    control={
                      <Checkbox
                        checked={selectedCsvSummaryItems.includes(item.type)}
                        onChange={() => handleItemToggle(item.type)}
                        size="small"
                      />
                    }
                    label={item.label}
                    sx={{ fontSize: '0.875rem', ml: 0 }}
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
      
      <FormErrorMessage errorMsg={errorMsg} visible={!!errorMsg} />
    </Box>
  );
};
